import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Bell, X } from 'lucide-react';
import { Tooltip as UITooltip, TooltipTrigger, TooltipContent } from './ui/tooltip';
import webPushService from '../services/webPushService';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const DISMISS_KEY = 'push_prompt_dismissed';
const DISMISS_DAYS = 14; // Re-ask after 14 days

// Event name other components can dispatch to force-show the prompt
// (bypassing the initial 2.5s delay and the dismiss cooldown).
// Still respects: permission already granted/denied, not already subscribed.
export const PUSH_PROMPT_EVENT = 'trainedbyrs:requestPushPrompt';

export function requestPushPrompt() {
  try {
    window.dispatchEvent(new CustomEvent(PUSH_PROMPT_EVENT));
  } catch {}
}

/**
 * Auto-prompt for push notifications.
 * Shows a bottom sheet on first visit (or 14 days after last dismiss).
 * Shows on all devices (desktop + mobile) where push is supported and not yet enabled.
 */
export default function PushPrompt() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(async () => {
      // Only if push is supported
      if (!webPushService.isSupported()) return;

      // Only if permission hasn't been decided yet
      const perm = webPushService.getPermission();
      if (perm === 'granted' || perm === 'denied') return;

      // Only if not already subscribed
      await webPushService.init();
      if (webPushService.isSubscribed()) return;

      // Check dismiss cooldown
      const dismissed = localStorage.getItem(DISMISS_KEY);
      if (dismissed) {
        const daysSince = (Date.now() - parseInt(dismissed)) / (1000 * 60 * 60 * 24);
        if (daysSince < DISMISS_DAYS) return;
      }

      setShow(true);
    }, 2500); // Show after 2.5s (let user see the app first)

    // External trigger: force-show when another component requests it
    // (e.g. after the user follows someone and push isn't enabled yet).
    // Bypasses the dismiss cooldown but still respects browser permission state
    // and existing subscription.
    const handleForceShow = async () => {
      if (!webPushService.isSupported()) return;
      const perm = webPushService.getPermission();
      if (perm === 'granted' || perm === 'denied') return;
      await webPushService.init();
      if (webPushService.isSubscribed()) return;
      setShow(true);
    };
    window.addEventListener(PUSH_PROMPT_EVENT, handleForceShow);

    return () => {
      clearTimeout(timer);
      window.removeEventListener(PUSH_PROMPT_EVENT, handleForceShow);
    };
  }, []);

  const handleEnable = async () => {
    setLoading(true);
    const result = await webPushService.subscribe();
    setLoading(false);

    if (result.success) {
      toast.success(t('push.enabled'));
      setShow(false);
    } else if (result.error === 'blocked') {
      toast.error(t('push.blocked'));
      setShow(false);
    } else if (result.error === 'dismissed') {
      // User dismissed Chrome's permission dialog
      dismiss();
    } else {
      toast.error(result.error || t('push.couldNotEnable'));
      dismiss();
    }
  };

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  if (!show) return null;

  return (
    <div className="fixed left-0 right-0 bottom-0 z-[99] px-4 pointer-events-none animate-in slide-in-from-bottom-4" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 68px)' }} data-testid="push-prompt-overlay">
      <div className="pointer-events-auto w-full max-w-md mx-auto rounded-2xl bg-card border border-border shadow-2xl p-5 space-y-4" data-testid="push-prompt">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-xl bg-primary/10 flex-shrink-0">
            <Bell className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-base">{t('push.title')}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {t('push.description')}
            </p>
          </div>
          <UITooltip>
            <TooltipTrigger asChild>
              <button onClick={dismiss} className="text-muted-foreground hover:text-foreground p-1 -mt-1 -mr-1" data-testid="push-prompt-dismiss">
                <X className="h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t('common.dismiss')}</TooltipContent>
          </UITooltip>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleEnable}
            disabled={loading}
            className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold"
            data-testid="push-prompt-enable"
          >
            {loading ? t('push.enabling') : t('push.enableBtn')}
          </Button>
          <Button variant="outline" onClick={dismiss} className="flex-shrink-0" data-testid="push-prompt-later">
            {t('push.later')}
          </Button>
        </div>
      </div>
    </div>
  );
}
